// /Users/stuartmcgeer/portalworx/frontend/src/contexts/SettingsContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axiosInstance from "../services/axiosInstance";

interface SettingsContextProps {
    dateFormat: string;
    setDateFormat: (format: string) => void;
    timeFormat: string;
    setTimeFormat: (format: string) => void;
    hasUnsavedChanges: boolean;
    saveSettings: () => Promise<void>;
    resetSettings: () => void;
    isSaveSuccessful: boolean;
    setIsSaveSuccessful: (value: boolean) => void;
    formatDate: (dateString: string) => string; // Centralized date formatting function
    formatTime: (timeString: string) => string; // Centralized time formatting function
}

const SettingsContext = createContext<SettingsContextProps | undefined>(undefined);

const defaultSettings = {
    dateFormat: "MM-DD-YYYY",
    timeFormat: "24-hour",
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [dateFormat, setDateFormat] = useState(defaultSettings.dateFormat);
    const [timeFormat, setTimeFormat] = useState(defaultSettings.timeFormat);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);

    // Fetch settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            if (!localStorage.getItem('token')) return; // Exit if no token is present

            try {
                const response = await axiosInstance.get("/settings/");
                setDateFormat(response.data.date_format);
                setTimeFormat(response.data.time_format);
            } catch (error) {
                console.error("Error fetching settings:", error);
            }
        };

        fetchSettings();
    }, []);

    const markUnsaved = useCallback(() => {
        setUnsavedChanges(true);
    }, []);

    const saveSettings = async () => {
        try {
            const payload = { date_format: dateFormat, time_format: timeFormat };
            await axiosInstance.put("/settings/", payload);
            setUnsavedChanges(false);
            setIsSaveSuccessful(true);
        } catch (error) {
            console.error("Error saving settings:", error);
            setIsSaveSuccessful(false);
        }
    };

    const resetSettings = async () => {
        try {
            const response = await axiosInstance.get("/settings/");
            setDateFormat(response.data.date_format);
            setTimeFormat(response.data.time_format);
            setUnsavedChanges(false);
        } catch (error) {
            console.error("Error resetting settings:", error);
        }
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);

        switch (dateFormat) {
            case "MM-DD-YYYY":
                return `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}-${date.getFullYear()}`;
            case "DD-MM-YYYY":
                return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
            case "YYYY-MM-DD":
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            default:
                return date.toISOString().split("T")[0]; // Fallback
        }
    };

    const formatTime = (timeString: string): string => {
        if (!timeString || !timeString.includes(":")) {
            return "Invalid Time"; // Fallback for invalid or unexpected input
        }
    
        const [hours, minutes] = timeString.split(":").map(Number);
    
        if (isNaN(hours) || isNaN(minutes)) {
            return "Invalid Time"; // Fallback for non-numeric values
        }
    
        if (timeFormat === "24-hour") {
            return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        } else {
            const period = hours >= 12 ? "PM" : "AM";
            const adjustedHours = hours % 12 || 12; // Convert 0 to 12 for AM/PM
            return `${adjustedHours}:${String(minutes).padStart(2, "0")} ${period}`;
        }
    };
    

    return (
        <SettingsContext.Provider
            value={{
                dateFormat,
                setDateFormat: (format) => {
                    setDateFormat(format);
                    markUnsaved();
                },
                timeFormat,
                setTimeFormat: (format) => {
                    setTimeFormat(format);
                    markUnsaved();
                },
                hasUnsavedChanges: unsavedChanges,
                saveSettings,
                resetSettings,
                isSaveSuccessful,
                setIsSaveSuccessful,
                formatDate, // Provide the centralized date formatter
                formatTime, // Provide the centralized time formatter
            }}
        >
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error("useSettings must be used within a SettingsProvider");
    }
    return context;
};

